import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const STEPS = ["Workspace", "First client", "First project"];

export default function Onboarding() {
  const nav = useNavigate();
  const { refresh, memberships } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [ws, setWs] = useState({ name: "", currency: "INR", use_case: "agency", role_preset: "starter" });
  const [client, setClient] = useState({ name: "", contact_email: "" });
  const [project, setProject] = useState({ name: "", description: "" });
  const [wsId, setWsId] = useState(memberships?.[0]?.workspace_id || null);
  const [clientId, setClientId] = useState(null);

  const next = async () => {
    setBusy(true);
    try {
      if (step === 0) {
        // Send the browser's IANA timezone so the workspace picks up the user's
        // actual TZ rather than defaulting to Asia/Kolkata.
        const browserTz = (() => {
          try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
          catch { return null; }
        })();
        const { data } = await api.post("/workspaces", { ...ws, timezone: browserTz });
        setWsId(data.id);
        await refresh();
        toast.success("Workspace created — head to Settings → Roles to customise your roles before inviting the team.", { duration: 8000 });
        setStep(1);
      } else if (step === 1) {
        const { data } = await api.post(`/workspaces/${wsId}/clients`, client);
        setClientId(data.id);
        setStep(2);
      } else if (step === 2) {
        await api.post(`/clients/${clientId}/projects`, { ...project, client_id: clientId });
        toast.success("You're all set!");
        nav("/app");
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save"));
    } finally {
      setBusy(false);
    }
  };

  const skip = () => nav("/app");

  return (
    <div className="min-h-screen bg-background grid place-items-center px-6 py-16">
      <div className="w-full max-w-xl">
        <div className="label-eyebrow mb-3">Step {step + 1} of {STEPS.length}</div>
        <h1 className="heading-display text-4xl sm:text-5xl mb-8" data-testid="onboarding-title">
          {step === 0 && "Name your workspace."}
          {step === 1 && "Add your first client."}
          {step === 2 && "Create your first project."}
        </h1>
        <div className="h-1 w-full bg-muted mb-10">
          <div className="h-full bg-primary transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>

        {step === 0 && (
          <div className="space-y-5">
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Workspace name</Label>
              <Input required value={ws.name} onChange={(e) => setWs({ ...ws, name: e.target.value })} className="h-11" data-testid="onb-ws-name" placeholder="Acme Studio" />
            </div>
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Use case</Label>
              <Select value={ws.use_case} onValueChange={(v) => setWs({ ...ws, use_case: v })}>
                <SelectTrigger className="h-11" data-testid="onb-ws-usecase"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="freelancer">Freelancer / Consultant</SelectItem>
                  <SelectItem value="design_studio">Design Studio</SelectItem>
                  <SelectItem value="agency">Agency</SelectItem>
                  <SelectItem value="video_team">Video Team</SelectItem>
                  <SelectItem value="brand_team">Brand / Marketing Team</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Role preset</Label>
              <Select value={ws.role_preset} onValueChange={(v) => setWs({ ...ws, role_preset: v })}>
                <SelectTrigger className="h-11" data-testid="onb-ws-role-preset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter — 7 roles, small teams</SelectItem>
                  <SelectItem value="agency">Agency — adds Department Head, for studios &amp; consultancies</SelectItem>
                  <SelectItem value="full_erp">Full ERP — deeper Sales/CRM + Vault for in-house ops</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-foreground/60 mt-1">You can rename, retune or delete any role later in Settings → Roles. Pick the closest match for now.</p>
            </div>
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Currency</Label>
              <Select value={ws.currency} onValueChange={(v) => setWs({ ...ws, currency: v })}>
                <SelectTrigger className="h-11" data-testid="onb-ws-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">INR — Indian Rupees</SelectItem>
                  <SelectItem value="USD">USD — US Dollars</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Client name</Label>
              <Input required value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} className="h-11" data-testid="onb-client-name" placeholder="Acme Coffee Co." />
            </div>
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Contact email (optional)</Label>
              <Input type="email" value={client.contact_email} onChange={(e) => setClient({ ...client, contact_email: e.target.value })} className="h-11" data-testid="onb-client-email" />
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Project name</Label>
              <Input required value={project.name} onChange={(e) => setProject({ ...project, name: e.target.value })} className="h-11" data-testid="onb-project-name" placeholder="Spring Campaign 2026" />
            </div>
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Brief description (optional)</Label>
              <Textarea rows={3} value={project.description} onChange={(e) => setProject({ ...project, description: e.target.value })} data-testid="onb-project-desc" />
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mt-10">
          <button type="button" onClick={skip} className="text-sm text-muted-foreground hover:text-foreground" data-testid="onb-skip-btn">
            Skip for now →
          </button>
          <Button onClick={next} disabled={busy} className="h-11 rounded-none px-6 font-bold uppercase tracking-widest text-xs" data-testid="onb-next-btn">
            {busy ? "Saving…" : step === STEPS.length - 1 ? "Finish" : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
